import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Mail, Phone, ShieldCheck, ChevronRight, BadgeCheck, Zap } from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—";

export function Profile() {
  const { user, logout } = useAuth();
  const { data: sub, isPro } = useSubscription();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    toast.success("Disconnesso");
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-6 sm:max-w-md sm:mx-auto" data-testid="profile-page">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 flex items-center gap-4">
        <div className="w-16 h-16 rounded-3xl bg-whoo text-white flex items-center justify-center font-heading font-bold text-2xl" data-testid="profile-avatar">
          {(user.name || "U")[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <h1 className="font-heading text-xl font-bold text-slate-900 truncate" data-testid="profile-name">{user.name}</h1>
          <p className="text-sm text-slate-500 capitalize" data-testid="profile-role">{user.role === "professional" ? "Professionista" : "Cliente"}</p>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="profile-info">
        <div className="flex items-center gap-3 p-4">
          <Mail className="w-5 h-5 text-slate-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-900 truncate">{user.email}</p>
            <p className="text-xs text-slate-400">Email</p>
          </div>
          {user.email_verified && <BadgeCheck className="w-4 h-4 text-emerald-500" />}
        </div>
        <div className="flex items-center gap-3 p-4">
          <Phone className="w-5 h-5 text-slate-400" />
          <div className="flex-1">
            <p className="text-sm text-slate-900">{user.phone || "Non impostato"}</p>
            <p className="text-xs text-slate-400">Telefono</p>
          </div>
          {user.phone_verified && <BadgeCheck className="w-4 h-4 text-emerald-500" />}
        </div>
      </div>

      {user.role === "professional" && (
        <Link to="/pro/abbonamento" data-testid="profile-subscription-card" className="block bg-white rounded-3xl border border-slate-100 shadow-sm p-5 active:scale-[0.99] transition-transform">
          <div className="flex items-center gap-3">
            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${isPro ? "bg-whoo text-white" : "bg-slate-100 text-slate-400"}`}>
              <Zap className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-slate-900" data-testid="profile-subscription-title">
                {isPro ? `WhooNow Pro ${sub?.plan_label || ""}` : "WhooNow Free"}
              </p>
              <p className="text-xs text-slate-500" data-testid="profile-subscription-detail">
                {isPro
                  ? sub?.cancel_at_period_end
                    ? `Termina il ${fmtDate(sub?.current_period_end)}`
                    : `Prossimo rinnovo: ${fmtDate(sub?.current_period_end)}`
                  : "Passa a Pro: più opportunità, zero commissioni"}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </div>
        </Link>
      )}

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm divide-y divide-slate-100">
        {user.role === "professional" && (
          <Link to="/pro" data-testid="profile-pro-dashboard-link" className="flex items-center gap-3 p-4 active:bg-slate-50 transition-colors">            <ShieldCheck className="w-5 h-5 text-whoo" />
            <span className="flex-1 text-sm font-semibold text-slate-900">Area professionista</span>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </Link>
        )}
        {user.role === "professional" && (
          <Link to="/pro/abbonamento" data-testid="profile-subscription-link" className="flex items-center gap-3 p-4 active:bg-slate-50 transition-colors">
            <Zap className="w-5 h-5 text-whoo" />
            <span className="flex-1 text-sm font-semibold text-slate-900">Il mio abbonamento</span>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </Link>
        )}
        <Link to="/reset-password" data-testid="profile-reset-password-link" className="flex items-center gap-3 p-4 active:bg-slate-50 transition-colors">
          <Mail className="w-5 h-5 text-slate-400" />
          <span className="flex-1 text-sm font-semibold text-slate-900">Cambia password</span>
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </Link>
      </div>

      <Button data-testid="logout-btn" variant="outline" onClick={doLogout}
        className="w-full h-12 rounded-full text-red-600 border-red-200 hover:bg-red-50 font-semibold">
        <LogOut className="w-4 h-4 mr-2" /> Esci
      </Button>

      <p className="text-center text-xs text-slate-400 pb-4">WhooNow · web, iOS e Android con lo stesso account</p>
    </div>
  );
}
